<template>
  <div class="app-wrapper" :class="{ dark: isDarkMode }">
    <AppHeader
      v-if="isInitialized && !isLoginRoute && !isCajero"
      :isDarkMode="isDarkMode"
      :sidebarExpanded="expanded"
      :isLoginRoute="isLoginRoute"
      @logout="solicitarCierreSoporte"
    />

    <AppSidebar
      v-if="isInitialized && shouldShowSidebar"
      :class="{ 'hidden-sidebar': !showSidebar }"
      :isDarkMode="isDarkMode"
      :expanded="expanded"
      :dropdowns="dropdowns"
      :has-permission="hasPermission"
      :is-active="isActive"
      :isLoginRoute="isLoginRoute"
      @toggle-sidebar="toggleSidebar"
      @open-dropdown="openDropdown"
      @close-dropdown="closeDropdown"
      @logout="solicitarCierreSoporte"
      @toggle-dark-mode="toggleDarkMode"
      @expand-sidebar="expandSidebar"
      @collapse-sidebar="collapseSidebar"
    />

    <!-- ── Banner de alerta proforma (trial día 10-15 o cobro mensual día 15-20) ── -->
    <transition name="banner-slide">
      <div
        v-if="proformaAlerta && !isLoginRoute && role === '4'"
        class="proforma-banner"
        :class="proformaAlerta.tipo === 'trial' ? 'banner-trial' : 'banner-mensual'"
      >
        <div class="banner-inner">
          <i :class="proformaAlerta.tipo === 'trial' ? 'bi bi-hourglass-split' : 'bi bi-receipt'"></i>
          <span class="banner-msg">{{ proformaAlerta.mensaje }}</span>
          <button class="banner-btn" @click="irAProforma">
            Ver proforma y pagar
          </button>
          <button class="banner-close" @click="proformaAlerta = null" title="Cerrar">×</button>
        </div>
      </div>
    </transition>

    <main
      class="main-content"
      :class="{
        expanded,
        dark: isDarkMode,
        login: isLoginRoute,
        'no-sidebar': !showSidebar || !shouldShowSidebar,
        'full-bleed': isFullBleed,
        'no-header': isCajero,
        'has-banner': !!proformaAlerta && !isLoginRoute && role === '4',
      }"
    >
      <router-view v-if="isInitialized" />
      <div v-else class="loading-state">
        <div class="loader"></div>
      </div>
    </main>

    <!-- ── Widget notas soporte ── -->
    <transition name="sp-slide">
      <div v-if="esSesionSoporte && !isLoginRoute" class="sp-notas-widget" :class="{ minimized: notasMinimized }">
        <div class="sp-notas-header" @click="notasMinimized = !notasMinimized">
          <span class="sp-badge"><i class="bi bi-headset"></i> Soporte</span>
          <div class="sp-context">
            <span class="sp-empresa">
              <i class="bi bi-building"></i>
              {{ soporteEmpresa || '—' }}
              <template v-if="soporteSucursal && soporteSucursal !== '(acceso CEO empresa)'"> · {{ soporteSucursal }}</template>
            </span>
            <span class="sp-agente" v-if="soporteAgente">
              <i class="bi bi-person-fill"></i> {{ soporteAgente }}
            </span>
          </div>
          <i class="bi" :class="notasMinimized ? 'bi-chevron-up' : 'bi-chevron-down'" style="margin-left:auto;font-size:.75rem;flex-shrink:0"></i>
        </div>
        <div v-if="!notasMinimized" class="sp-notas-body">
          <textarea
            v-model="notasSoporte"
            class="sp-textarea"
            placeholder="Escribe aquí lo que hiciste en esta sesión…"
            @input="guardarNotasEnSession"
          ></textarea>
          <div class="sp-notas-footer">
            <span class="sp-hint"><i class="bi bi-info-circle"></i> Se incluirán en el reporte al cerrar</span>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── Modal aprobación acceso soporte ── -->
    <transition name="sp-fade">
      <div v-if="modalSolicitudSoporte && solicitudActual" class="sp-overlay">
        <div class="sp-modal sp-aprobacion-modal">
          <div class="sp-modal-header">
            <i class="bi bi-shield-exclamation" style="color:#f59e0b;font-size:1.4rem"></i>
            <h3>Solicitud de acceso soporte</h3>
            <span v-if="solicitudesPendientes.length > 1" class="sp-queue-badge">
              {{ solicitudesPendientes.length }} pendientes
            </span>
          </div>
          <p class="sp-aprobacion-desc">
            Un agente de <strong>Perdomo &amp; Asociados</strong> quiere conectarse a su sistema.
          </p>
          <div class="sp-aprobacion-info">
            <div class="sp-aprobacion-row">
              <i class="bi bi-person-badge-fill"></i>
              <span><strong>Agente:</strong> {{ solicitudActual.agente_perdomo }}</span>
            </div>
            <div class="sp-aprobacion-row" v-if="solicitudActual.empresa">
              <i class="bi bi-building"></i>
              <span><strong>Empresa:</strong> {{ solicitudActual.empresa }}</span>
            </div>
            <div class="sp-aprobacion-row" v-if="solicitudActual.sucursal">
              <i class="bi bi-geo-alt-fill"></i>
              <span><strong>Sucursal:</strong> {{ solicitudActual.sucursal }}</span>
            </div>
            <div class="sp-aprobacion-row">
              <i class="bi bi-person-gear"></i>
              <span><strong>Perfil:</strong> {{ solicitudActual.rol_soporte }}</span>
            </div>
          </div>
          <div class="sp-aprobacion-timer" :class="{ urgent: countdownSolicitud < 30 }">
            <i class="bi bi-clock"></i>
            Expira en {{ Math.floor(countdownSolicitud / 60) }}:{{ String(countdownSolicitud % 60).padStart(2, '0') }}
          </div>
          <p class="sp-aprobacion-nota">
            <i class="bi bi-info-circle"></i>
            Solo apruebe si usted solicitó este soporte. En caso de duda, rechace.
          </p>
          <div class="sp-modal-btns">
            <button class="sp-btn-rechazar" @click="responderSolicitudSoporte('rejected')">
              <i class="bi bi-x-lg"></i> Rechazar
            </button>
            <button class="sp-btn-aprobar" @click="responderSolicitudSoporte('approved')">
              <i class="bi bi-check-lg"></i> Aprobar acceso
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── Modal cierre soporte ── -->
    <transition name="sp-fade">
      <div v-if="modalCierreSoporte" class="sp-overlay">
        <div class="sp-modal">
          <div class="sp-modal-header">
            <i class="bi bi-clipboard-check-fill" style="color:#f59e0b;font-size:1.3rem"></i>
            <h3>Cerrar sesión de soporte</h3>
          </div>
          <div class="sp-modal-meta" v-if="soporteEmpresa">
            <span><i class="bi bi-building"></i> {{ soporteEmpresa }}</span>
            <span v-if="soporteSucursal && soporteSucursal !== '(acceso CEO empresa)'">
              <i class="bi bi-geo-alt-fill"></i> {{ soporteSucursal }}
            </span>
            <span v-if="soporteAgente"><i class="bi bi-person-fill"></i> {{ soporteAgente }}</span>
          </div>
          <p class="sp-modal-sub">Agrega notas sobre lo que hiciste antes de salir. Quedarán registradas en el informe.</p>
          <textarea
            v-model="notasSoporte"
            class="sp-textarea sp-textarea-lg"
            placeholder="Ej: Revisé ventas del día, corregí productos sin precio, configuré impresora…"
            @input="guardarNotasEnSession"
          ></textarea>
          <div class="sp-modal-btns">
            <button class="sp-btn-cancel" @click="modalCierreSoporte = false">Cancelar</button>
            <button class="sp-btn-confirm" @click="confirmarCierre">
              <i class="bi bi-box-arrow-right"></i> Guardar y cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import AppSidebar from "./components/AppSidebar.vue";
import AppHeader from "./components/AppHeader.vue";
import axios from "axios";
import solicitudes from "../services/solicitudes";
const { connectSocket, disconnectSocket, getSocket } = require("./services/socket");

export default {
  name: "App",
  components: {
    AppSidebar,
    AppHeader,
  },
  data() {
    return {
      isInitialized: false,
      expanded: false,
      proformaAlerta: null, // { tipo: 'trial'|'mensual', mensaje, nombre_plan, precio_hnl, fecha_limite }
      dropdowns: {
        ventas: false,
        compras: false,
      },
      isDarkMode: localStorage.getItem('isDarkMode') === 'true',
      userRole: null,
      userPermissions: null,
      authChecked: false,
      // Soporte — sesión y cierre
      notasSoporte: '',
      notasMinimized: false,
      modalCierreSoporte: false,
      esperandoFoco: false,
      cierreIntencional: false,
      hasSoporteSession: false,
      // Soporte — aprobación de acceso (cola para múltiples solicitudes simultáneas)
      solicitudesPendientes: [],
      countdownSolicitud: 300,
      countdownSolicitudTimer: null,
    };
  },

  async created() {
    try {
      const savedRole = localStorage.getItem("role");
      const token = localStorage.getItem("auth");

      if (token && savedRole) {
        this.userRole = savedRole;
        this.isInitialized = true;

        if (!window.location.pathname.includes('/login')) {
          await this.checkAuthAndInitialize();
        }
      } else {
        this.isInitialized = true;
        if (!window.location.pathname.includes('/login')) {
          await this.$router.push("/login");
        }
      }

      window.addEventListener("storage", this.handleStorageChange);
      window.addEventListener("roleChange", this.handleRoleChange);
      window.addEventListener("beforeunload", this.onBeforeUnload);
      window.addEventListener("focus", this.onFocusRetorno);
      window.addEventListener("soporte-logout-request", this.solicitarCierreSoporte);
      // poswave:toggle-dark: VentasList lo emite cuando el cajero cambia el tema
      window.addEventListener('poswave:toggle-dark', this.syncDarkFromStorage);
      window.addEventListener('poswave:proforma-alerta', this.handleProformaAlerta);

      // Si ya hay una proforma activa al cargar (p.ej. recarga de página), mostrar banner
      if (savedRole === '4') {
        const estadoSusc = localStorage.getItem('estado_suscripcion');
        if (estadoSusc === 'proforma_activa') {
          this.proformaAlerta = {
            tipo: 'trial',
            mensaje: 'Tu período de prueba está por vencer. Ingresa a Mi Cuenta para ver la proforma y pagar.',
          };
        } else if (estadoSusc === 'proforma_pendiente') {
          this.proformaAlerta = {
            tipo: 'mensual',
            mensaje: 'Tu factura mensual está lista. Tienes hasta el día 20 para pagar.',
          };
        }
      }
      if (token) {
        connectSocket();
        this.$nextTick(() => {
          this.setupSoporteListener();
          this.setupSuscripcionListener();
        });
      }
      this.cargarNotasSesion();
    } catch (error) {
      console.error("Error en created:", error);
      this.isInitialized = true;
    }
  },

  computed: {
    role() {
      return this.userRole || localStorage.getItem('role');
    },

    isLoginRoute() {
      return this.$route.path === "/login";
    },

    solicitudActual() {
      return this.solicitudesPendientes[0] || null;
    },

    modalSolicitudSoporte() {
      return this.solicitudesPendientes.length > 0;
    },

    esSesionSoporte() {
      return this.hasSoporteSession;
    },
    soporteAgente() {
      try {
        const s = JSON.parse(sessionStorage.getItem('soporte_session') || '{}');
        return s.agente_perdomo || '';
      } catch { return ''; }
    },
    soporteEmpresa() {
      try {
        const s = JSON.parse(sessionStorage.getItem('soporte_session') || '{}');
        return s.empresa || '';
      } catch { return ''; }
    },
    soporteSucursal() {
      try {
        const s = JSON.parse(sessionStorage.getItem('soporte_session') || '{}');
        return s.sucursal || '';
      } catch { return ''; }
    },
    isAuthenticated() {
      const token = localStorage.getItem("auth");
      const role = localStorage.getItem("role") || this.userRole;
      return !!(token && role);
    },

    empresaVencida() {
      // Si hay comprobante en revisión → no bloquear el sidebar
      const comprobantePendiente = localStorage.getItem('comprobante_pendiente') === '1';
      const estadoSusc = localStorage.getItem('estado_suscripcion');
      const tieneComprobanteActivo = comprobantePendiente ||
        estadoSusc === 'pago_en_revision' ||
        estadoSusc === 'aprobado_autenticidad';
      if (tieneComprobanteActivo) return false;

      const fv = localStorage.getItem('fecha_vencimiento');
      if (!fv) return false;
      // Parseo local para evitar el bug UTC (new Date("YYYY-MM-DD") interpreta como UTC midnight)
      // Todos los roles tienen 5 días de gracia — solo se bloquea al vencer ese período
      const [y, m, d] = String(fv).substring(0, 10).split('-').map(Number);
      const venc = new Date(y, m - 1, d + 5, 23, 59, 59, 999);
      return venc < new Date();
    },

    isCajero() {
      const role = this.userRole || localStorage.getItem('role');
      // Cajero siempre, o Admin/CEO cuando están en la vista de caja
      return role === '3' || ((role === '1' || role === '4') && this.$route?.path === '/ventas');
    },

    isFullBleed() {
      return this.$route?.path === '/documentacion';
    },

    shouldShowSidebar() {
      const role = this.userRole || localStorage.getItem("role");
      const token = localStorage.getItem("auth");
      const isAuth = !!(token && role);
      const enCajaComoAdmin = (role === '1' || role === '4') && this.$route?.path === '/ventas';

      // Ocultar sidebar cuando la empresa está vencida (pantalla /plan-vencido no necesita nav)
      return isAuth && !this.isLoginRoute && role && role !== "3" && !enCajaComoAdmin && !this.empresaVencida;
    },

    showSidebar() {
      const role = this.userRole || localStorage.getItem("role");
      const token = localStorage.getItem("auth");
      return token && role && role !== "3";
    },

    hasSidebarPermission() {
      const permissions = {
        1: true,
        2: true,
        3: false,
        4: true,
      };
      return permissions[this.userRole] || false;
    },
  },

  methods: {
    async checkAuthAndInitialize() {
      try {
        const token = localStorage.getItem("auth");
        const role = localStorage.getItem("role");

        if (!token || !role) {
          this.isInitialized = true;
          if (!window.location.pathname.includes('/login')) {
            await this.$router.push("/login");
          }
          return;
        }

        try {
          const response = await axios.get(
            `${solicitudes.homeUrl}/token/verify-token`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.data) {
            this.userRole = role;
            this.isInitialized = true;
            await this.initializeApp();

            // Redirigir a ventas si es cajero (role 3)
            if (role === "3" && this.$route.path === "/home") {
              await this.$router.push("/ventas");
            }
          }
        } catch (error) {
          console.error("Error en verificación:", error);
          await this.logout();
        }
      } catch (error) {
        console.error("Error en checkAuthAndInitialize:", error);
        this.isInitialized = true;
        if (!window.location.pathname.includes('/login')) {
          await this.$router.push("/login");
        }
      }
    },

    async initializeApp() {
      try {
        if (!this.isAuthenticated) {
          this.userRole = null;
          return;
        }

        const role = localStorage.getItem("role");
        if (role) {
          this.userRole = role;

          this.$nextTick(() => {
            this.expanded = false;
            this.dropdowns = {
              ventas: false,
              compras: false,
            };
          });
        }
      } catch (error) {
        console.error("Error en initializeApp:", error);
        await this.logout();
      }
    },

    handleRoleChange() {
      const newRole = localStorage.getItem("role");
      if (newRole !== this.userRole) {
        this.userRole = newRole;
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },

    // ── Proforma: redirigir a Mi Cuenta para pagar ────────────────────────────
    irAProforma() {
      this.$router.push('/mi-cuenta').catch(() => {});
    },

    // ── Listener del evento poswave:proforma-alerta ───────────────────────────
    handleProformaAlerta(e) {
      this.proformaAlerta = e.detail;
    },

    handleStorageChange(event) {
      if (event.key === "auth" || event.key === "role") {
        this.checkAuthAndInitialize();
        if (event.key === "auth" && event.newValue) connectSocket();
        if (event.key === "auth" && !event.newValue) disconnectSocket();
      }
    },

    hasPermission(section) {
      const role = Number(this.userRole || localStorage.getItem("role"));
      // Solo el CEO nunca se le bloquea nada
      if (role === 4) return true;

      // Verificar permisos personalizados primero
      try {
        const customPermisos = JSON.parse(localStorage.getItem("user_permisos") || "null");
        if (customPermisos?.modulos) {
          return customPermisos.modulos[section] === true;
        }
      } catch { /* ignora JSON inválido */ }

      // Defaults por rol (sin permisos custom configurados)
      const defaults = {
        1: ["Home","Usuario","Categorias","Productos","Clientes","Proveedores","Compra","Venta","reporte","CuentasPorCobrar","Cotizaciones","ArqueoCaja","CuentasPorPagar","Devoluciones","Nomina","LibrosFiscales","Auditoria"],
        2: ["Home","Productos","Clientes","Proveedores","Compra","Venta","reporte","CuentasPorCobrar","Cotizaciones","ArqueoCaja","CuentasPorPagar","Devoluciones"],
        3: [], // cajero no tiene sidebar
      };
      return defaults[role]?.includes(section) ?? false;
    },

    hasAccionPermiso(accion) {
      const role = Number(this.userRole || localStorage.getItem("role"));
      // Solo el CEO nunca se le bloquea nada
      if (role === 4) return true;
      try {
        const customPermisos = JSON.parse(localStorage.getItem("user_permisos") || "null");
        if (customPermisos?.acciones && accion in customPermisos.acciones) {
          return customPermisos.acciones[accion] === true;
        }
      } catch { /* ignora */ }
      const defaults = {
        1: { compras_crear:true, productos_crear:false, productos_editar:false, productos_eliminar:false, categorias_crear:false, categorias_editar:false, categorias_eliminar:false, clientes_crear:true, clientes_editar:true, clientes_eliminar:true, proveedores_crear:true, proveedores_editar:true, proveedores_eliminar:true, cotizaciones_crear:true, cotizaciones_gestionar:true, cxp_pagar:true, cxc_cobrar:true, devoluciones_crear:true, nomina_generar:true, nomina_pagar:true, nomina_eliminar:true, usuarios_crear:true, usuarios_editar:true, usuarios_eliminar:true, sucursales_crear:false, sucursales_editar:false, sucursales_eliminar:false },
        2: { ventas_descuento_general:true, ventas_descuento_producto:true, ventas_nc:true, ventas_cotizaciones:true, ventas_crear_cliente:true, compras_crear:true, productos_crear:true, productos_editar:true, productos_eliminar:true, categorias_crear:false, categorias_editar:false, categorias_eliminar:false, clientes_crear:true, clientes_editar:true, clientes_eliminar:true, proveedores_crear:true, proveedores_editar:true, proveedores_eliminar:true, cotizaciones_crear:true, cotizaciones_gestionar:true, cxp_pagar:true, cxc_cobrar:true, devoluciones_crear:true, nomina_generar:false, nomina_pagar:false, nomina_eliminar:false, usuarios_crear:false, usuarios_editar:false, usuarios_eliminar:false, sucursales_crear:false, sucursales_editar:false, sucursales_eliminar:false },
        3: { ventas_descuento_general:false, ventas_descuento_producto:false, ventas_nc:true, ventas_cotizaciones:true, ventas_crear_cliente:true },
      };
      return defaults[role]?.[accion] ?? false;
    },

    async logout() {
      try {
        const token = localStorage.getItem("auth");
        if (token) {
          await axios.post(
            `${solicitudes.homeUrl}/token/logout`,
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        }
      } catch (error) {
        console.error("Error al cerrar sesión:", error);
      } finally {
        const darkPref = localStorage.getItem('isDarkMode');
        localStorage.clear();
        if (darkPref !== null) localStorage.setItem('isDarkMode', darkPref);
        this.userRole = null;
        this.expanded = false;
        this.isInitialized = false;
        await this.$router.push("/login");
        this.$nextTick(() => {
          this.isInitialized = true;
        });
      }
    },

    toggleSidebar() {
      this.expanded = !this.expanded;
    },

    isActive(route) {
      return this.$route.path === route;
    },

    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem("isDarkMode", this.isDarkMode);
      window.dispatchEvent(new CustomEvent('poswave:toggle-dark'));
    },

    syncDarkFromStorage() {
      const val = localStorage.getItem('isDarkMode') === 'true';
      if (this.isDarkMode !== val) this.isDarkMode = val;
    },

    openDropdown(dropdown) {
      this.dropdowns[dropdown] = true;
    },

    closeDropdown(dropdown) {
      this.dropdowns[dropdown] = false;
    },

    expandSidebar() {
      this.expanded = true;
    },

    collapseSidebar() {
      this.expanded = false;
    },

    // ── Suscripción en tiempo real ────────────────────────────────────
    setupSuscripcionListener() {
      const socket = getSocket();
      if (!socket) return;

      // Plan actualizado en tiempo real (cambio de plan aprobado por Perdomo)
      socket.on('plan:actualizado', (payload) => {
        const { plan_config } = payload || {};
        if (plan_config) {
          localStorage.setItem('empresa_plan', JSON.stringify(plan_config));
          localStorage.setItem('estado_suscripcion', 'activa');
          // Forzar re-render del sidebar emitiendo evento de cambio
          window.dispatchEvent(new Event('roleChange'));
          window.dispatchEvent(new CustomEvent('poswave:toast', {
            detail: { tipo: 'success', texto: 'Tu plan ha sido actualizado. El menú se actualizó automáticamente.' }
          }));
        }
      });

      // ── Proforma trial disponible (día 10 del trial) ─────────────────────────
      // La empresa pasa a 'proforma_activa': sigue con acceso pero la proforma ya está lista
      socket.on('proforma:disponible', (payload) => {
        const { mensaje, fecha_limite, nombre_plan, precio_hnl, tipo_cambio } = payload || {};
        localStorage.setItem('estado_suscripcion', 'proforma_activa');
        window.dispatchEvent(new Event('roleChange'));
        // Banner persistente (el CEO debe ver esto y actuar)
        window.dispatchEvent(new CustomEvent('poswave:proforma-alerta', {
          detail: {
            tipo:        'trial',
            nombre_plan,
            precio_hnl,
            tipo_cambio,
            fecha_limite,
            mensaje:     mensaje || `Tu período de prueba está por vencer. Proforma lista hasta el ${fecha_limite}.`,
          }
        }));
      });

      // ── Proforma mensual (día 15 de cada mes) ────────────────────────────────
      // La empresa pasa a 'proforma_pendiente': tiene 5 días para subir comprobante
      socket.on('proforma:mensual', (payload) => {
        const { mensaje, fecha_limite, nombre_plan, precio_hnl } = payload || {};
        localStorage.setItem('estado_suscripcion', 'proforma_pendiente');
        window.dispatchEvent(new Event('roleChange'));
        window.dispatchEvent(new CustomEvent('poswave:proforma-alerta', {
          detail: {
            tipo:        'mensual',
            nombre_plan,
            precio_hnl,
            fecha_limite,
            mensaje:     mensaje || `Tu factura mensual está lista. Paga antes del ${fecha_limite}.`,
          }
        }));
      });

      socket.on('suscripcion:estado', (payload) => {
        const { estado, nueva_fecha_vencimiento, plan_config, mensaje } = payload || {};

        if (estado === 'activa' && nueva_fecha_vencimiento) {
          // Aprobado → actualizar fecha, limpiar bloqueo, notificar
          localStorage.setItem('fecha_vencimiento', nueva_fecha_vencimiento + 'T12:00:00.000Z');
          localStorage.setItem('estado_suscripcion', 'activa');
          localStorage.removeItem('comprobante_pendiente');
          localStorage.removeItem('estado_bloqueo');
          // Actualizar plan_config si viene en el payload (puede haber cambiado con la renovación)
          if (plan_config) {
            localStorage.setItem('empresa_plan', JSON.stringify(plan_config));
          }
          // Forzar re-render del sidebar
          window.dispatchEvent(new Event('roleChange'));
          // Redirigir a home si estaban bloqueados (plan-vencido) o
          // en mi-cuenta subiendo comprobante después de un rechazo
          const RUTAS_BLOQUEO = ['/plan-vencido', '/mi-cuenta'];
          if (RUTAS_BLOQUEO.includes(this.$route?.path)) {
            this.$router.push('/home');
          }
          // Toast de éxito
          window.dispatchEvent(new CustomEvent('poswave:toast', {
            detail: { tipo: 'success', texto: mensaje || '¡Suscripción renovada! Acceso restaurado.' }
          }));

        } else if (['rechazado', 'bloqueada_mora', 'bloqueada_revision'].includes(estado)) {
          // Rechazado o bloqueado → limpiar comprobante, guardar estado, redirigir
          localStorage.removeItem('comprobante_pendiente');
          localStorage.setItem('estado_suscripcion', estado === 'rechazado' ? 'bloqueada_mora' : estado);
          // Guardar el estado específico para que PlanVencido.vue muestre el mensaje correcto
          localStorage.setItem('estado_bloqueo', estado);
          window.dispatchEvent(new CustomEvent('poswave:toast', {
            detail: { tipo: 'error', texto: mensaje || 'Tu comprobante fue rechazado. Acceso suspendido.' }
          }));
          setTimeout(() => {
            if (this.$route?.path !== '/plan-vencido') {
              this.$router.push('/plan-vencido');
            }
          }, 2500);

        } else if (estado === 'pago_en_revision') {
          // Inconsistencia revertida → comprobante vuelve a pendiente, acceso restaurado
          localStorage.setItem('comprobante_pendiente', '1');
          localStorage.removeItem('estado_bloqueo');
          if (this.$route?.path === '/plan-vencido') this.$router.push('/plan-vencido'); // reload estado
          window.dispatchEvent(new CustomEvent('poswave:toast', {
            detail: { tipo: 'info', texto: mensaje || 'La inconsistencia fue descartada. Tu comprobante está en revisión normal.' }
          }));
        }
      });
    },

    // ── Aprobación acceso soporte ─────────────────────────────────────
    setupSoporteListener() {
      const socket = getSocket();
      if (!socket) return;

      socket.on('soporte:solicitud', (payload) => {
        const role = localStorage.getItem('role');
        if (role !== '1') return; // Solo admins ven el modal
        // Evitar duplicados si el evento llega dos veces
        if (!this.solicitudesPendientes.find(s => s.session_id === payload.session_id)) {
          this.solicitudesPendientes.push(payload);
        }
      });

      // Otro admin ya respondió — cerrar el modal si lo tenemos abierto
      socket.on('soporte:solicitud_resuelta', ({ session_id }) => {
        const idx = this.solicitudesPendientes.findIndex(s => s.session_id === session_id);
        if (idx !== -1) this.solicitudesPendientes.splice(idx, 1);
      });
    },

    async responderSolicitudSoporte(decision) {
      clearInterval(this.countdownSolicitudTimer);
      const actual = this.solicitudActual;
      if (!actual) return;
      // Sacar de la cola inmediatamente para que el watcher muestre la siguiente
      this.solicitudesPendientes.shift();
      try {
        const token = localStorage.getItem('auth');
        await fetch(`${solicitudes.homeUrl}/soporte-acceso/responder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ session_id: actual.session_id, decision }),
        });
      } catch { /* ignorar — el agente recibirá el estado por polling */ }
    },

    // ── Sesión soporte ────────────────────────────────────────────────
    getSoporteSession() {
      try { return JSON.parse(sessionStorage.getItem('soporte_session')); }
      catch { return null; }
    },

    registrarPaginaSoporte(ruta) {
      const s = this.getSoporteSession();
      if (!s) return;
      s.paginas.push({ ruta, tiempo: new Date().toISOString() });
      sessionStorage.setItem('soporte_session', JSON.stringify(s));
    },

    guardarNotasEnSession() {
      const s = this.getSoporteSession();
      if (!s) return;
      s.notas = this.notasSoporte;
      sessionStorage.setItem('soporte_session', JSON.stringify(s));
    },

    buildReporte(estado = 'completada') {
      const s = this.getSoporteSession();
      if (!s) return null;
      const fin = new Date().toISOString();
      return {
        ...s,
        notas: this.notasSoporte || s.notas || '',
        fin,
        duracion_minutos: Math.round((new Date(fin) - new Date(s.inicio)) / 60000),
        estado,
      };
    },

    enviarBeacon(reporte) {
      if (!reporte) return;
      const blob = new Blob([JSON.stringify(reporte)], { type: 'application/json' });
      navigator.sendBeacon(`${solicitudes.homeUrl}/adminUsuariosP/sesion-soporte`, blob);
    },

    // beforeunload: muestra diálogo nativo y envía beacon preventivo
    onBeforeUnload(e) {
      if (!this.esSesionSoporte || this.cierreIntencional) return;
      // Beacon preventivo con las notas actuales (por si el usuario confirma salir)
      this.enviarBeacon(this.buildReporte('interrumpida'));
      // Mostrar diálogo nativo para que pueda quedarse y escribir notas
      e.preventDefault();
      e.returnValue = '¿Cerrar la sesión de soporte? Puedes quedarte para agregar notas al informe.';
      this.esperandoFoco = true;
    },

    // Si el usuario eligió "Quedarse" en el diálogo nativo, la página recupera el foco
    onFocusRetorno() {
      if (!this.esperandoFoco || !this.esSesionSoporte) return;
      this.esperandoFoco = false;
      // Cargar notas actuales y mostrar modal
      const s = this.getSoporteSession();
      this.notasSoporte = s?.notas || this.notasSoporte;
      this.modalCierreSoporte = true;
    },

    // Botón cerrar sesión
    solicitarCierreSoporte() {
      if (this.esSesionSoporte) {
        const s = this.getSoporteSession();
        this.notasSoporte = s?.notas || this.notasSoporte;
        this.modalCierreSoporte = true;
      } else {
        this.logout();
      }
    },

    // Confirmar desde el modal (logout intencional)
    async confirmarCierre() {
      this.modalCierreSoporte = false;
      this.guardarNotasEnSession();
      const reporte = this.buildReporte('completada');
      this.cierreIntencional = true; // para que beforeunload no interfiera
      this.enviarBeacon(reporte);
      sessionStorage.removeItem('soporte_session');
      this.hasSoporteSession = false;
      await this.logout();
    },

    cargarNotasSesion() {
      const s = this.getSoporteSession();
      if (s) { this.hasSoporteSession = true; }
      if (s?.notas) this.notasSoporte = s.notas;
    },
  },

  beforeUnmount() {
    window.removeEventListener("storage", this.handleStorageChange);
    window.removeEventListener("roleChange", this.handleRoleChange);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    window.removeEventListener("focus", this.onFocusRetorno);
    window.removeEventListener("soporte-logout-request", this.solicitarCierreSoporte);
    window.removeEventListener('poswave:toggle-dark', this.syncDarkFromStorage);
    window.removeEventListener('poswave:proforma-alerta', this.handleProformaAlerta);
    disconnectSocket();
  },

  watch: {
    // Dark mode: sincronizar body background para que no quede blanco en móvil
    isDarkMode: {
      immediate: true,
      handler(val) {
        document.body.style.backgroundColor = val ? '#141414' : '';
        document.body.style.color = val ? '#e5e5e5' : '';
      }
    },

    // Cada vez que cambia la solicitud al frente de la cola, reinicia el countdown
    solicitudActual(newVal) {
      clearInterval(this.countdownSolicitudTimer);
      if (!newVal) return;
      this.countdownSolicitud = Math.max(0, Math.floor((new Date(newVal.expira) - Date.now()) / 1000));
      this.countdownSolicitudTimer = setInterval(() => {
        this.countdownSolicitud--;
        if (this.countdownSolicitud <= 0) {
          clearInterval(this.countdownSolicitudTimer);
          this.solicitudesPendientes.shift();
        }
      }, 1000);
    },

    $route: {
      immediate: true,
      async handler(to) {
        if (to.path !== "/login") {
          const token = localStorage.getItem("auth");
          const role = localStorage.getItem("role");

          if (!token || !role) {
            // Skip push if browser URL is already /login (preserves ?u=&p=&agente= params)
            if (!window.location.pathname.includes('/login')) {
              await this.$router.push("/login");
            }
          } else if (!this.isInitialized) {
            await this.checkAuthAndInitialize();
          } else {
            if (role && this.userRole !== role) {
              this.userRole = role;
            }
            if (role === "3" && to.path === "/home") {
              await this.$router.push("/ventas");
            }
          }
          // Sincronizar estado reactivo del widget soporte
          this.hasSoporteSession = !!this.getSoporteSession();
          // Registrar página visitada si es sesión soporte
          this.registrarPaginaSoporte(to.path);
        }
      },
    },

    isAuthenticated: {
      immediate: true,
      handler(newValue) {
        if (!newValue && !window.location.pathname.includes('/login')) {
          this.$router.push("/login");
        }
      },
    },

    userRole: {
      immediate: true,
      handler(newRole) {
        if (newRole) {
          this.$forceUpdate();
        }
      },
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

* {
  font-family: "Montserrat", sans-serif;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.loader {
  border: 4px solid #f3f3f3;
  border-radius: 50%;
  border-top: 4px solid #c09d62;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.app-wrapper {
  display: flex;
  flex-direction: row;
  height: 100vh;
  width: 100%; /* era 100vw — incluye scrollbar y causa desborde de ~17px */
  overflow-x: hidden; /* nunca scroll horizontal en el wrapper raíz */
  overflow-y: auto;
  margin: 0;
  padding: 0;
  position: relative;
  background-color: #f5f5f5;
  transition: background-color 0.3s ease;
}

.app-wrapper.dark {
  background-color: #1e1e1e;
}

.main-content {
  margin-left: 80px;
  padding: 20px;
  padding-top: 72px; /* 52px header + 20px gap */
  width: calc(100% - 80px); /* evita desborde: margin-left + width:100% sumaban 80px extra */
  transition: margin-left 0.3s ease, width 0.3s ease, background-color 0.3s ease;
  background-color: inherit;
  min-height: 100vh;
  position: relative;
  box-sizing: border-box;
}

.main-content.expanded {
  margin-left: 200px;
  width: calc(100% - 200px);
}

.main-content.dark {
  background-color: #1e1e1e;
  color: #ffffff;
}

.main-content.login {
  margin-left: 0;
  padding: 0;
  padding-top: 0;
}

.main-content.full-bleed {
  padding: 0;
  padding-top: 52px;
  overflow: hidden;
  height: 100vh;
}

.main-content.no-sidebar {
  margin-left: 0;
  width: 100%;
}

.main-content.no-header {
  padding: 0;
}

/* ── Banner proforma (trial día 10-15 / cobro mensual día 15-20) ────────── */
.proforma-banner {
  position: fixed;
  top: 52px; /* debajo del header */
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 0 16px;
  display: flex;
  justify-content: center;
}

.banner-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 0 0 10px 10px;
  font-size: 13px;
  font-weight: 500;
  max-width: 900px;
  width: 100%;
  box-shadow: 0 4px 16px rgba(0,0,0,.18);
}

.banner-trial {
  .banner-inner {
    background: linear-gradient(135deg, #92400e 0%, #d97706 100%);
    color: #fff;
  }
}

.banner-mensual {
  .banner-inner {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    color: #fff;
  }
}

.banner-inner i { font-size: 16px; flex-shrink: 0; }

.banner-msg {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.banner-btn {
  background: rgba(255,255,255,.2);
  border: 1px solid rgba(255,255,255,.4);
  color: #fff;
  border-radius: 6px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background .2s;
}
.banner-btn:hover { background: rgba(255,255,255,.35); }

.banner-close {
  background: none;
  border: none;
  color: rgba(255,255,255,.8);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  flex-shrink: 0;
}
.banner-close:hover { color: #fff; }

/* Cuando hay banner, empujar el main-content hacia abajo */
.main-content.has-banner { padding-top: 100px; }
.main-content.has-banner.full-bleed { padding-top: 100px; }

/* Animación del banner */
.banner-slide-enter-active, .banner-slide-leave-active {
  transition: transform .25s ease, opacity .25s ease;
}
.banner-slide-enter-from, .banner-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

:deep(.dark) {
  background-color: #1e1e1e;
  color: #ffffff;
}

:deep(.dark table) {
  background-color: #2d2d2d;
  color: #ffffff;
}

:deep(.dark th),
:deep(.dark td) {
  border-color: #404040;
}

:deep(.dark .btn) {
  background-color: #404040;
  color: #ffffff;
}

:deep(.dark input),
:deep(.dark select),
:deep(.dark textarea) {
  background-color: #2d2d2d;
  color: #ffffff;
  border-color: #404040;
}

:deep(.dark .card) {
  background-color: #2d2d2d;
  border-color: #404040;
}

:deep(.dark .modal-content) {
  background-color: #2d2d2d;
  border-color: #404040;
}

/* ── Widget soporte ─────────────────────────────────────────────────── */
.sp-notas-widget {
  position: fixed;
  bottom: 20px; right: 20px;
  width: 300px;
  background: #1c1c2e;
  border: 1px solid rgba(245,158,11,.4);
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,.5);
  z-index: 8000;
  overflow: hidden;
  transition: height .2s ease;
}
.sp-notas-widget.minimized { width: 220px; }

.sp-notas-header {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; cursor: pointer;
  background: rgba(245,158,11,.08);
  border-bottom: 1px solid rgba(245,158,11,.2);
  user-select: none;
}
.sp-badge {
  font-size: .72rem; font-weight: 700;
  color: #fbbf24;
  display: flex; align-items: center; gap: 5px;
}
.sp-context {
  display: flex; flex-direction: column; gap: 1px;
  min-width: 0; overflow: hidden;
}
.sp-agente {
  font-size: .68rem; color: #9ca3af;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.sp-empresa {
  font-size: .65rem; color: #fbbf24;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  display: flex; align-items: center; gap: 4px;
}

.sp-notas-body { padding: 10px; display: flex; flex-direction: column; gap: 6px; }
.sp-textarea {
  width: 100%; min-height: 90px; max-height: 200px;
  background: rgba(255,255,255,.05);
  border: 1px solid rgba(245,158,11,.25);
  border-radius: 8px; padding: 8px 10px;
  color: #e5e7eb; font-size: .8rem; font-family: inherit;
  resize: vertical; outline: none;
  transition: border-color .2s;
}
.sp-textarea:focus { border-color: #f59e0b; }
.sp-textarea-lg { min-height: 130px; }
.sp-notas-footer { display: flex; justify-content: flex-end; }
.sp-hint { font-size: .67rem; color: #6b7280; display: flex; align-items: center; gap: 4px; }

/* ── Modal cierre ── */
.sp-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.65);
  display: flex; align-items: center; justify-content: center;
  z-index: 9500;
}
.sp-modal {
  background: #1c1c2e;
  border: 1px solid rgba(245,158,11,.35);
  border-radius: 18px;
  padding: 28px;
  width: 440px; max-width: 95vw;
  display: flex; flex-direction: column; gap: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,.6);
}
.sp-modal-header { display: flex; align-items: center; gap: 12px; }
.sp-modal-header h3 { font-size: 1rem; font-weight: 700; color: #f3f4f6; margin: 0; }
.sp-modal-meta {
  display: flex; flex-wrap: wrap; gap: 10px;
  background: rgba(245,158,11,.07);
  border: 1px solid rgba(245,158,11,.2);
  border-radius: 8px; padding: 8px 12px;
  font-size: .76rem; color: #fbbf24;
}
.sp-modal-meta span { display: flex; align-items: center; gap: 5px; }
.sp-modal-sub { font-size: .83rem; color: #9ca3af; margin: 0; line-height: 1.5; }
.sp-modal-btns { display: flex; justify-content: flex-end; gap: 10px; }
.sp-btn-cancel {
  padding: 9px 18px; border-radius: 10px; border: 1px solid #374151;
  background: transparent; color: #9ca3af; font-size: .83rem; cursor: pointer;
}
.sp-btn-cancel:hover { background: rgba(255,255,255,.05); }
.sp-btn-confirm {
  padding: 9px 20px; border-radius: 10px; border: none;
  background: #f59e0b; color: #1c1c2e; font-size: .83rem; font-weight: 700;
  cursor: pointer; display: flex; align-items: center; gap: 7px;
  transition: background .15s;
}
.sp-btn-confirm:hover { background: #d97706; }

/* ── Modal aprobación soporte ── */
.sp-aprobacion-modal { max-width: 400px; }
.sp-queue-badge {
  margin-left: auto; font-size: .72rem; font-weight: 700;
  background: rgba(245,158,11,.15); color: #f59e0b;
  border: 1px solid rgba(245,158,11,.3); border-radius: 20px;
  padding: 2px 9px; white-space: nowrap;
}
.sp-aprobacion-desc  { font-size: .88rem; color: #9ca3af; margin-bottom: 14px; line-height: 1.5; }
.sp-aprobacion-info  { background: #1a1a1a; border: 1px solid #2d2d2d; border-radius: 8px; padding: 12px 14px; margin-bottom: 14px; display: flex; flex-direction: column; gap: 8px; }
.sp-aprobacion-row   { display: flex; align-items: center; gap: 10px; font-size: .84rem; color: #d1d5db; }
.sp-aprobacion-row i { color: #c09d62; font-size: .9rem; width: 16px; flex-shrink: 0; }
.sp-aprobacion-timer {
  text-align: center; font-size: 1rem; font-weight: 700;
  color: #c09d62; margin-bottom: 10px; font-variant-numeric: tabular-nums;
}
.sp-aprobacion-timer.urgent { color: #ef4444; }
.sp-aprobacion-nota  { font-size: .78rem; color: #6b7280; margin-bottom: 18px; display: flex; align-items: flex-start; gap: 7px; line-height: 1.45; }
.sp-aprobacion-nota i { color: #f59e0b; flex-shrink: 0; margin-top: 2px; }
.sp-btn-rechazar {
  padding: 8px 16px; border-radius: 7px; border: 1px solid #374151;
  background: transparent; color: #9ca3af; font-size: .85rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all .15s;
}
.sp-btn-rechazar:hover { background: rgba(239,68,68,.1); border-color: #ef4444; color: #ef4444; }
.sp-btn-aprobar {
  padding: 8px 18px; border-radius: 7px; border: none;
  background: linear-gradient(135deg, #c09d62, #d4b07a);
  color: #fff; font-size: .85rem; font-weight: 700;
  cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all .15s;
}
.sp-btn-aprobar:hover { background: linear-gradient(135deg, #d4b07a, #c09d62); transform: translateY(-1px); }

/* Transiciones */
.sp-slide-enter-active, .sp-slide-leave-active { transition: all .3s ease; }
.sp-slide-enter-from, .sp-slide-leave-to { opacity: 0; transform: translateY(20px); }
.sp-fade-enter-active, .sp-fade-leave-active { transition: opacity .2s ease; }
.sp-fade-enter-from, .sp-fade-leave-to { opacity: 0; }

/* Media Queries */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    width: 100%; /* sin sidebar en móvil */
    padding: 15px;
  }

  .main-content.expanded {
    margin-left: 0;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .main-content {
    padding: 10px;
  }
}

/* Optimizaciones para pantallas táctiles */
@media (hover: none) {
  * {
    -webkit-tap-highlight-color: transparent;
  }
}

/* Soporte para dispositivos de alta densidad */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

/* Ajustes para mejor rendimiento */
.main-content {
  -webkit-overflow-scrolling: touch;
  will-change: margin-left;
}

/* Mejoras de accesibilidad */
@media (prefers-reduced-motion: reduce) {
  .main-content,
  .loader {
    transition: none;
  }
}
</style>